import { showError } from "@/utils/toastUtils";
import React from "react";
import { useGetDecisionExplainQuery } from "../../../api/nebula-stats/nebulaStatsApi";
import { reformatDate } from "../utils/dateUtils";
import { EmptyState } from "./EmptyState";
import { WarningTriangleFilledDotIcon } from "./icons/WarningTriangleFilledDotIcon";
import NebulaModal from "./NebulaModal";
import PlanDetailBlockRenderer from "./PlanDetailBlockRenderer";
import Spinner from "./Spinner";

const ACTION_STYLE = {
  buy: { label: "MUA", accent: "#34D399", barBg: "#34D3990D" },
  sell: { label: "BÁN", accent: "#FF6B6B", barBg: "#FF6B6B0D" },
  hold: { label: "GIỮ", accent: "#636369", barBg: "#3C3C430D" },
};

export const unwrapExplain = (res) => res?.data?.data ?? res?.data ?? null;

export const splitTableBlock = (block) => {
  const rows = block?.content?.rows;
  if (!Array.isArray(rows) || rows.length < 4) return [block];

  const [header, ...body] = rows;
  const half = Math.ceil(body.length / 2);
  const build = (slice, suffix) => ({
    ...block,
    id: `${block.id}-${suffix}`,
    content: { ...block.content, rows: [header, ...slice] },
  });

  return [build(body.slice(0, half), "a"), build(body.slice(half), "b")];
};

const getBlockText = (block) => {
  if (!Array.isArray(block?.content)) return "";
  return block.content.map((item) => item?.text ?? "").join("");
};

const normalizeSearchText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .toLowerCase();

const isNebulaReasonHeading = (value) =>
  normalizeSearchText(value).trim().startsWith("vi sao nebula ");

const removeNebulaReasonHeadingBlocks = (blocks) =>
  Array.isArray(blocks)
    ? blocks.filter(
        (block) =>
          block?.type !== "heading" ||
          !isNebulaReasonHeading(getBlockText(block)),
      )
    : [];

const splitSummaryRiskBlocks = (blocks) => {
  if (!Array.isArray(blocks)) return [];
  return blocks.flatMap((block) => {
    if (block?.type !== "paragraph" || !Array.isArray(block.content))
      return [block];

    const index = block.content.findIndex((item) =>
      /Rủi ro chính\s*:/.test(item?.text ?? ""),
    );
    if (index < 0) return [block];

    const item = block.content[index];
    const [before, after] = String(item.text).split(/Rủi ro chính\s*:/);
    const first = [
      ...block.content.slice(0, index),
      ...(before.trimEnd() ? [{ ...item, text: before.trimEnd() }] : []),
    ];
    const second = [
      { type: "text", text: "Rủi ro chính:", styles: { bold: true } },
      ...(after.trimStart()
        ? [{ ...item, styles: {}, text: ` ${after.trimStart()}` }]
        : []),
      ...block.content.slice(index + 1),
    ];

    return [
      { ...block, id: `${block.id}-a`, content: first },
      { ...block, id: `${block.id}-b`, content: second },
    ];
  });
};

const getStatusColor = (status) => {
  const normalized = normalizeSearchText(status);
  if (/cat lo|thua lo/.test(normalized)) return "red";
  if (/chot loi|co lai|sinh loi/.test(normalized)) return "green";
  return "gray";
};

export const normalizeInvestmentResultBlocks = (blocks) => {
  if (!Array.isArray(blocks)) return [];

  const tableIndex = blocks.findIndex(
    (block) =>
      block?.type === "table" && block.content?.rows?.[0]?.cells?.length === 2,
  );
  const statusIndex = blocks.findIndex((block) =>
    /^trang thai\s*:/i.test(normalizeSearchText(getBlockText(block))),
  );

  if (tableIndex < 0 || statusIndex < 0) return blocks;

  const status = getBlockText(blocks[statusIndex])
    .replace(/^\s*Trạng thái\s*:\s*/i, "")
    .replace(/[.\s]+$/, "");
  if (!status) return blocks;

  const table = blocks[tableIndex];
  const statusRow = {
    cells: [
      "Trạng thái",
      [
        {
          type: "text",
          text: status,
          styles: { textColor: getStatusColor(status) },
        },
      ],
    ],
  };

  return blocks
    .map((block, index) =>
      index === tableIndex
        ? {
            ...table,
            content: {
              ...table.content,
              rows: [...table.content.rows, statusRow],
            },
          }
        : block,
    )
    .filter((_, index) => index !== statusIndex);
};

const titleContentGap = (blocks) => {
  const first = (Array.isArray(blocks) ? blocks : []).find(Boolean);
  if (first?.type === "table") return "gap-[16px]";
  if (
    first?.type === "paragraph" &&
    getBlockText(first).trim().startsWith("Ngành:")
  )
    return "gap-[4px]";
  return "gap-[8px]";
};

const SectionTitle = ({ size = 16, children }) => (
  <h3
    className="font-bold leading-[24px] text-[#111827]"
    style={{ fontSize: size }}
  >
    {children}
  </h3>
);

const FINANCIAL_COLOR_SECTION_KEYS = new Set(["overview", "positions"]);

const PortfolioSection = ({ section }) => {
  const isOverview = section.key === "overview";
  const colorizeFinancialValues = FINANCIAL_COLOR_SECTION_KEYS.has(section.key);
  const blocks = Array.isArray(section.blocks) ? section.blocks : [];

  if (!isOverview) {
    return (
      <section className={`flex flex-col pt-[32px] ${titleContentGap(blocks)}`}>
        <SectionTitle size={18}>{section.title}</SectionTitle>
        <PlanDetailBlockRenderer
          blocks={blocks}
          colorizeFinancialValues={colorizeFinancialValues}
        />
      </section>
    );
  }

  const intro = blocks.filter((block) => block.type !== "table");
  const table = blocks.find((block) => block.type === "table");
  const halves = table ? splitTableBlock(table) : [];

  return (
    <section className="flex flex-col pt-[16px] gap-[16px]">
      <div className={`flex flex-col ${titleContentGap(intro)}`}>
        <SectionTitle size={18}>{section.title}</SectionTitle>
        {intro.length > 0 && (
          <PlanDetailBlockRenderer
            blocks={intro}
            colorizeFinancialValues={colorizeFinancialValues}
          />
        )}
      </div>
      <div className="grid grid-cols-1 gap-[24px] md:grid-cols-2">
        {halves.map((half) => (
          <PlanDetailBlockRenderer
            key={half.id}
            blocks={[half]}
            colorizeFinancialValues={colorizeFinancialValues}
          />
        ))}
      </div>
    </section>
  );
};

const TickerBar = ({ ticker, action, tradeDate }) => (
  <div
    className="-mx-[40px] flex items-center gap-[8px] px-[40px] py-[8px]"
    style={{ backgroundColor: action.barBg }}
  >
    <span
      className="h-[12px] w-[12px] shrink-0 rounded-full"
      style={{ backgroundColor: action.accent }}
    />
    <span className="text-[18px] font-bold leading-[24px] text-[#111827]">
      {ticker.toUpperCase()}
    </span>
    <span
      className="rounded-[8px] px-[8px] py-[2px] text-[14px] font-bold leading-[20px] text-white"
      style={{ backgroundColor: action.accent }}
    >
      {action.label}
    </span>
    {tradeDate && (
      <span className="text-[14px] leading-[20px] text-[#636369]">
        - {tradeDate}
      </span>
    )}
  </div>
);

const DISCLAIMER_KEY = "disclaimer";

const DisclaimerSection = ({ section }) => (
  <div className="flex items-start justify-center pt-[40px]">
    <WarningTriangleFilledDotIcon
      color="#FBBF24"
      size={14}
      className="mt-[2px] shrink-0 -mr-[4px]"
    />
    <div className="max-w-[720px] text-center text-[12px] leading-[18px] text-[#636369] [&_p]:m-0 [&_p]:!text-[#636369]">
      <PlanDetailBlockRenderer
        blocks={Array.isArray(section.blocks) ? section.blocks : []}
        tableStyle="card"
        colorizeFinancialValues={false}
      />
    </div>
  </div>
);

const RiskAdjustmentSection = ({ section }) => (
  <section className="-mt-[8px] rounded-[12px] bg-[#F5F5F5] px-[20px] py-[12px] text-[14px] leading-[20px] text-[#636369] [&>div]:contents [&_p]:inline [&_p]:!text-[#636369]">
    <strong className="font-[700] text-[#111827]">{section.title}:</strong>{" "}
    <PlanDetailBlockRenderer
      blocks={Array.isArray(section.blocks) ? section.blocks : []}
      colorizeFinancialValues={false}
    />
  </section>
);

const RisksSection = ({ section }) => (
  <section className={`flex flex-col ${titleContentGap(section.blocks)}`}>
    <SectionTitle>Rủi ro</SectionTitle>
    <PlanDetailBlockRenderer
      blocks={Array.isArray(section.blocks) ? section.blocks : []}
      colorizeFinancialValues={false}
      asList
    />
  </section>
);

const TickerBlock = ({ ticker, detail, tradeDate }) => {
  const action = ACTION_STYLE[detail?.action] ?? ACTION_STYLE.hold;
  const allSections = detail?.contentJson?.sections ?? [];
  const sections = allSections.filter(
    (section) => section.key !== DISCLAIMER_KEY,
  );
  const lead = sections.filter((section) => !section.title);
  const titled = sections.filter((section) => section.title);

  return (
    <div className="pt-[32px] flex flex-col gap-[24px]">
      <div className="flex flex-col gap-[16px]">
        <TickerBar ticker={ticker} action={action} tradeDate={tradeDate} />
        {lead.map((section) => (
          <PlanDetailBlockRenderer
            key={section.key}
            blocks={removeNebulaReasonHeadingBlocks(section.blocks)}
            colorizeFinancialValues={false}
          />
        ))}
      </div>

      {titled.map((section) =>
        section.key === "risks" ? (
          <RisksSection key={section.key} section={section} />
        ) : section.key === "risk_adjustment" ? (
          <RiskAdjustmentSection key={section.key} section={section} />
        ) : (
          <section
            key={section.key}
            className={`flex flex-col ${titleContentGap(section.blocks)} ${
              section.key === "summary" ? "-mt-[8px]" : ""
            } ${
              section.key === "analysis" || section.key === "summary"
                ? ""
                : "[&_p]:pl-[20px]"
            }`}
          >
            {!isNebulaReasonHeading(section.title) && (
              <SectionTitle>{section.title}</SectionTitle>
            )}
            <PlanDetailBlockRenderer
              blocks={
                normalizeSearchText(section.title).includes(
                  "ket qua khoan dau tu",
                )
                  ? normalizeInvestmentResultBlocks(section.blocks)
                  : section.key === "summary"
                    ? splitSummaryRiskBlocks(
                        removeNebulaReasonHeadingBlocks(section.blocks),
                      )
                    : removeNebulaReasonHeadingBlocks(section.blocks)
              }
              colorizeFinancialValues={false}
            />
          </section>
        ),
      )}
    </div>
  );
};

const PlanDetailModal = ({ decisionId, onClose }) => {
  const { data, isFetching, isError } = useGetDecisionExplainQuery(decisionId, {
    skip: decisionId == null,
  });

  React.useEffect(() => {
    if (isError) showError("Không thể tải kế hoạch");
  }, [isError]);

  const plan = React.useMemo(() => unwrapExplain(data), [data]);
  const portfolioSections = plan?.portfolio?.sections ?? [];
  const tradeDate = plan?.asOf
    ? reformatDate(plan.asOf, "DD/MM/YYYY", "YYYY-MM-DD")
    : "";
  const tickers = React.useMemo(
    () => Object.entries(plan?.tickers ?? {}),
    [plan],
  );
  const disclaimer = React.useMemo(
    () =>
      tickers
        .flatMap(([, detail]) => detail?.contentJson?.sections ?? [])
        .find((section) => section.key === DISCLAIMER_KEY),
    [tickers],
  );
  const isEmpty = portfolioSections.length === 0 && tickers.length === 0;

  return (
    <NebulaModal
      open={decisionId != null}
      onClose={onClose}
      title="Chi tiết kế hoạch"
      align="center"
      classNames={{
        root: "!h-[90vh] !max-w-[930px] !overflow-hidden",
        body: "-mx-[24px] flex-1 min-h-0 min-w-0 overflow-x-hidden overflow-y-auto thin-scrollbar px-[24px]",
        header: "shrink-0 bg-white pb-[16px]",
      }}
      stickyHeader
    >
      {isFetching && (
        <div className="flex items-center justify-center py-[40px] h-[412px]">
          <Spinner />
        </div>
      )}

      {!isFetching && !isEmpty && (
        <div className="flex min-w-0 w-full flex-col px-[16px]">
          {portfolioSections.map((section) => (
            <PortfolioSection key={section.key} section={section} />
          ))}
          {tickers.map(([ticker, detail]) => (
            <TickerBlock
              key={ticker}
              ticker={ticker}
              detail={detail}
              tradeDate={tradeDate}
            />
          ))}
          {disclaimer && <DisclaimerSection section={disclaimer} />}
        </div>
      )}

      {!isFetching && isEmpty && (
        <EmptyState variant="padded" text="Không thể tải kế hoạch." />
      )}
    </NebulaModal>
  );
};

export default PlanDetailModal;
